import { useState, useCallback, useMemo } from 'react';
import { useTheme } from '@course-authoring/shared-types';
import { QuickSelectChips, AI_GENERATE_CHIPS } from './QuickSelectChips';
import type { SelectedImageData } from './imageInsertTypes';
import { AI_IMAGE_PROMPT_MAX_LENGTH, AI_IMAGE_PROMPT_TOO_LONG_MESSAGE } from '../api/aiImageApi';
import { useAutoGrowTextarea } from '../hooks/useAutoGrowTextarea';

export interface AIGenerateState {
  prompt: string;
  generatedImageUrl: string | null;
  generatedMimeType: string | null;
  isGenerating: boolean;
  error: string | null;
}

export function createInitialAIGenerateState(): AIGenerateState {
  return {
    prompt: '',
    generatedImageUrl: null,
    generatedMimeType: null,
    isGenerating: false,
    error: null,
  };
}

interface AIGeneratePanelProps {
  state: AIGenerateState;
  onStateChange: (state: AIGenerateState) => void;
  onGenerate: (prompt: string) => Promise<{ imageUrl: string; mimeType: string }>;
  onImageReady: (data: SelectedImageData) => void;
  targetAspectRatio?: string;
}

export function AIGeneratePanel({
  state,
  onStateChange,
  onGenerate,
  onImageReady,
  targetAspectRatio,
}: AIGeneratePanelProps) {
  const { tokens } = useTheme();
  const styles = useMemo(() => createStyles(tokens), [tokens]);
  const [localPrompt, setLocalPrompt] = useState(state.prompt);
  const textareaRef = useAutoGrowTextarea(localPrompt);
  const isPromptValid =
    localPrompt.trim().length > 0 && localPrompt.length <= AI_IMAGE_PROMPT_MAX_LENGTH;
  const charCount = localPrompt.length;
  const isOverLimit = charCount > AI_IMAGE_PROMPT_MAX_LENGTH;

  const updateState = useCallback(
    (updates: Partial<AIGenerateState>) => {
      onStateChange({ ...state, ...updates });
    },
    [state, onStateChange]
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!isPromptValid || state.isGenerating) return;

      updateState({ isGenerating: true, error: null, prompt: localPrompt.trim() });

      try {
        const result = await onGenerate(localPrompt.trim());
        updateState({
          generatedImageUrl: result.imageUrl,
          generatedMimeType: result.mimeType,
          isGenerating: false,
        });
      } catch (error) {
        updateState({
          error: error instanceof Error ? error.message : 'Failed to generate image',
          isGenerating: false,
        });
      }
    },
    [isPromptValid, localPrompt, state.isGenerating, updateState, onGenerate]
  );

  const handlePromptChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setLocalPrompt(e.target.value);
  }, []);

  const handleChipSelect = useCallback(
    (value: string) => {
      setLocalPrompt((prev) => {
        const trimmed = prev.trim();
        return trimmed ? `${trimmed}, ${value}` : value;
      });
    },
    []
  );

  const handleUseImage = useCallback(async () => {
    if (!state.generatedImageUrl) return;

    const response = await fetch(state.generatedImageUrl);
    const blob = await response.blob();

    onImageReady({
      previewUrl: state.generatedImageUrl,
      blob,
      needsUpload: true,
      suggestedAlt: '',
      aiGeneratedAlt: false,
      mimeType: state.generatedMimeType || 'image/png',
      sourceType: 'ai-generate',
    });
  }, [state.generatedImageUrl, state.generatedMimeType, localPrompt, onImageReady]);

  const handleTryAgain = useCallback(() => {
    updateState({ generatedImageUrl: null, generatedMimeType: null, error: null });
  }, [updateState]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault();
        if (isPromptValid && !state.isGenerating) {
          const form = e.currentTarget.closest('form');
          if (form) {
            form.requestSubmit();
          }
        }
      }
    },
    [isPromptValid, state.isGenerating]
  );

  // Show result view
  if (state.generatedImageUrl && !state.isGenerating) {
    return (
      <div style={styles.container}>
        <div style={styles.resultContainer} aria-label="Generated image result">
          <p style={styles.resultLabel} id="generated-image-label">Generated Image</p>
          <div style={styles.imageContainer}>
            <img
              src={state.generatedImageUrl}
              alt={`AI generated image: ${localPrompt}`}
              style={styles.generatedImage}
            />
          </div>
          <div style={styles.resultActions}>
            <button
              type="button"
              style={styles.tryAgainButton}
              onClick={handleTryAgain}
              aria-label="Try generating again"
            >
              Try Again
            </button>
            <button
              type="button"
              style={styles.useButton}
              onClick={handleUseImage}
              aria-label="Use this generated image"
            >
              Use This Image
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <form onSubmit={handleSubmit} style={styles.form}>
        <label style={styles.label}>
          <span style={styles.labelText} id="prompt-label">Describe the image you want to create</span>
          <textarea
            ref={textareaRef}
            value={localPrompt}
            onChange={handlePromptChange}
            onKeyDown={handleKeyDown}
            placeholder="e.g., A professional illustration of a team collaborating in a modern office..."
            style={{
              ...styles.textarea,
              ...(isOverLimit ? styles.textareaError : {}),
            }}
            rows={8}
            disabled={state.isGenerating}
            aria-labelledby="prompt-label"
            aria-describedby="char-count"
            aria-invalid={isOverLimit}
          />
          <div style={styles.textareaFooter}>
            <span style={styles.validationMessage} role="alert">
              {isOverLimit ? AI_IMAGE_PROMPT_TOO_LONG_MESSAGE : ''}
            </span>
            <span
              id="char-count"
              style={{
                ...styles.charCount,
                ...(isOverLimit ? styles.charCountError : {}),
              }}
              aria-live="polite"
            >
              {charCount}/{AI_IMAGE_PROMPT_MAX_LENGTH}
            </span>
          </div>
        </label>

        <QuickSelectChips
          chips={AI_GENERATE_CHIPS}
          onSelect={handleChipSelect}
          disabled={state.isGenerating}
        />

        {!state.isGenerating && (
          <button
            type="submit"
            style={{
              ...styles.generateButton,
              ...(!isPromptValid ? styles.generateButtonDisabled : {}),
            }}
            disabled={!isPromptValid}
            aria-label="Generate image from description"
          >
            Generate Image
          </button>
        )}
      </form>

      {state.error && (
        <div style={styles.errorMessage} role="alert" aria-live="assertive">
          {state.error}
        </div>
      )}

      {state.isGenerating && (
        <div style={styles.loadingContainer} aria-live="polite" role="status">
          <div style={styles.loadingSpinner} aria-hidden="true" />
          <p style={styles.loadingText}>Creating your image...</p>
          <p style={styles.loadingHint}>This may take a minute</p>
        </div>
      )}
    </div>
  );
}

function createStyles(tokens: ReturnType<typeof useTheme>['tokens']): Record<string, React.CSSProperties> {
  return {
    container: {
      display: 'flex',
      flexDirection: 'column',
      gap: '16px',
      padding: '20px',
      flex: 1,
      overflow: 'auto',
    },
    aspectRatioHint: {
      fontSize: '12px',
      color: tokens.text.subtle,
    },
    form: {
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
    },
    label: {
      display: 'flex',
      flexDirection: 'column',
      gap: '6px',
    },
    labelText: {
      fontSize: '14px',
      fontWeight: 500,
      color: tokens.text.primary,
    },
    textarea: {
      padding: '12px',
      fontSize: '14px',
      lineHeight: 1.5,
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: tokens.text.subtle,
      borderRadius: '6px',
      outline: 'none',
      fontFamily: 'inherit',
      resize: 'vertical',
      boxSizing: 'border-box',
      // Auto-grows with content (useAutoGrowTextarea) between an 8-line floor
      // (8 × 21px line + 24px padding + 2px border) and a viewport-relative cap.
      minHeight: '194px',
      maxHeight: '40vh',
      overflowY: 'auto',
      backgroundColor: tokens.backgrounds.app,
      color: tokens.text.primary,
    },
    textareaError: {
      borderColor: tokens.ui.errorBorder,
    },
    validationMessage: {
      fontSize: '12px',
      color: tokens.ui.errorText,
    },
    textareaFooter: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    hint: {
      fontSize: '12px',
      color: tokens.text.subtle,
    },
    charCount: {
      fontSize: '12px',
      color: tokens.text.subtle,
    },
    charCountError: {
      color: tokens.ui.errorText,
    },
    generateButton: {
      padding: '12px 20px',
      fontSize: '14px',
      fontWeight: 500,
      border: 'none',
      borderRadius: '6px',
      backgroundColor: tokens.ui.selectionBorder,
      color: tokens.text.onBrand,
      cursor: 'pointer',
      minWidth: '44px',
      minHeight: '44px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
    },
    generateButtonDisabled: {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
    spinner: {
      width: '16px',
      height: '16px',
      border: `2px solid color-mix(in srgb, ${tokens.text.onBrand} 30%, transparent)`,
      borderTopColor: tokens.text.onBrand,
      borderRadius: '50%',
      animation: 'spin 1s linear infinite',
    },
    errorMessage: {
      padding: '12px',
      backgroundColor: tokens.ui.errorBackground,
      border: `1px solid ${tokens.ui.errorBorder}`,
      borderRadius: '6px',
      color: tokens.ui.errorText,
      fontSize: '14px',
    },
    loadingContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '40px 20px',
      flex: 1,
    },
    loadingSpinner: {
      width: '48px',
      height: '48px',
      border: `4px solid ${tokens.text.subtle}`,
      borderTopColor: tokens.ui.selectionBorder,
      borderRadius: '50%',
      animation: 'spin 1s linear infinite',
      marginBottom: '16px',
    },
    loadingText: {
      fontSize: '16px',
      fontWeight: 500,
      color: tokens.text.primary,
      margin: 0,
    },
    loadingHint: {
      fontSize: '14px',
      color: tokens.text.subtle,
      marginTop: '8px',
    },
    resultContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
      flex: 1,
    },
    resultLabel: {
      fontSize: '14px',
      fontWeight: 500,
      color: tokens.text.primary,
      margin: 0,
    },
    imageContainer: {
      border: `2px solid ${tokens.text.subtle}`,
      borderRadius: '8px',
      overflow: 'hidden',
    },
    generatedImage: {
      width: '100%',
      maxHeight: '300px',
      objectFit: 'contain',
      display: 'block',
    },
    promptUsed: {
      fontSize: '13px',
      color: tokens.text.muted,
      margin: 0,
      lineHeight: 1.5,
    },
    resultActions: {
      display: 'flex',
      gap: '12px',
      justifyContent: 'flex-end',
    },
    tryAgainButton: {
      padding: '10px 20px',
      fontSize: '14px',
      fontWeight: 500,
      border: `1px solid ${tokens.text.subtle}`,
      borderRadius: '6px',
      background: 'none',
      cursor: 'pointer',
      color: tokens.text.muted,
      minWidth: '44px',
      minHeight: '44px',
    },
    useButton: {
      padding: '10px 20px',
      fontSize: '14px',
      fontWeight: 500,
      border: 'none',
      borderRadius: '6px',
      backgroundColor: tokens.ui.selectionBorder,
      color: tokens.text.onBrand,
      cursor: 'pointer',
      minWidth: '44px',
      minHeight: '44px',
    },
  };
}
